/**
 * The first thing a visitor reads: what Sparkie does, in one line, with the
 * signup form directly underneath (contracts/web-surface.md §2).
 *
 * Three elements and nothing else. The tone the spec sets — calm and archival,
 * "Vault", not SaaS-flashy — is mostly a matter of what is left out: no badge,
 * no eyebrow, no countdown.
 *
 * ## Why the headline is a sentence and not the wordmark
 *
 * It used to read "Sparkie". A product name tells a first-time visitor nothing,
 * and the name is already in the header on every page. The headline now names
 * the behaviour instead, and the sentence under it names the artifact you end
 * up with. That split — emotional line, functional line — is what every landing
 * page worth borrowing from does.
 *
 * `displayMedium`, not `displayLarge`: the headline grew from one word to a
 * seven-word sentence, and the larger step wraps to four lines at the 320 px
 * reflow floor, which reads as a wall rather than an opening.
 *
 * ## This is the page's one `<h1>` (FR-019(e))
 *
 * The wordmark in `site-header.tsx` is deliberately a brand *link* rather than
 * a heading, precisely so this one can be the `<h1>` on `/` without making two
 * per page. The not-found page's own heading plays the same role there.
 *
 * react-native-web picks the heading element from `aria-level`, so the pair
 * below is what turns this `Text` into a real `<h1>` rather than a `<div>` that
 * merely announces as one. Screen readers get the level either way; the element
 * is what document-outline tooling, "skip to heading" navigation and the
 * automated audit actually read.
 *
 * ## Who owns the layout
 *
 * The hero owns its own padding on both axes and centres what it contains. The
 * root shell adds no horizontal inset of its own, so a section that did not
 * would sit flush against the window edge — and `EmailCaptureForm` caps itself
 * at a readable measure and stays `width: "100%"` inside it, which only centres
 * because this column tells it to. The page container adds the 1100 px column
 * around these sections; it does not add a second horizontal inset, so the
 * `spacing.md` here is the whole gutter at every width, including the 320 px
 * reflow floor FR-019(f) sets.
 */

import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { EmailCaptureForm } from "@/components/marketing/email-capture-form";
import { PhoneMock } from "@/components/marketing/phone-mock";
import { headingProps } from "@/lib/heading-props";
import { useAppTheme } from "@/theme/paper-theme";
import { spacing } from "@/theme/tokens";
import { PROSE_MEASURE } from "@/theme/web-tokens";

const HEADLINE = "Say it out loud. It's already a note.";

// "Plain markdown file" is gone on purpose: the redesign keeps file-format
// vocabulary off the page entirely and lets the phone below do the showing.
// "Unless you turn it on" is load-bearing (61e9): premium note cleanup is a
// network feature, so the old absolute "no cloud" would oversell — the closing
// clause scopes it with the same phrase the privacy section's rule uses.
const SUBHEADLINE =
  "Speak your mind. Sparkie writes it down and files it with the notes it belongs with, right on your iPhone. No account needed, and no cloud unless you turn it on.";

/**
 * Prepared once and spread, rather than written inline, because `aria-level`
 * is not in react-native's prop types — `@/lib/heading-props` explains why and
 * hands the pair out to every heading on the site.
 */
const PAGE_HEADING_PROPS = headingProps(1);

export function Hero() {
  const theme = useAppTheme();

  return (
    <View style={styles.hero}>
      <View style={styles.intro}>
        <Text
          variant="displayMedium"
          style={[
            styles.centered,
            styles.headline,
            { color: theme.colors.onSurface },
          ]}
          {...PAGE_HEADING_PROPS}
        >
          {HEADLINE}
        </Text>

        {/* Quieter than the headline by colour rather than by size, so the two
            serif lines read as a title and its subtitle instead of as two
            headings competing. */}
        <Text
          variant="headlineSmall"
          style={[
            styles.centered,
            styles.subheadline,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          {SUBHEADLINE}
        </Text>
      </View>

      <EmailCaptureForm />

      <PhoneMock />
    </View>
  );
}

const styles = StyleSheet.create({
  hero: {
    alignItems: "center",
    // The gap the eye reads as "and now you do something", against the tighter
    // one inside `intro` that keeps the sentence attached to the headline.
    gap: spacing.xl,
    // Asymmetric on purpose: the space above is the page's opening breath, the
    // space below only has to separate the hero from the section that follows.
    paddingTop: spacing.xxxl,
    paddingBottom: spacing.xxl,
    paddingHorizontal: spacing.md,
  },
  intro: {
    alignItems: "center",
    gap: spacing.sm,
  },
  // Wide enough to land as one line on a desktop window. Held to the
  // subheadline's measure it broke after "already a", orphaning "note." — the
  // payoff word — on a line of its own.
  headline: {
    maxWidth: 900,
  },
  // A sentence, not a slogan: run it the width of the headline and it sits at a
  // measure nobody reads comfortably. The two caps differ on purpose, which is
  // why the constraint is on each child rather than on the box around them.
  subheadline: {
    maxWidth: PROSE_MEASURE,
  },
  // Paper's `Text` sets `textAlign: 'left'` on every variant, so centring the
  // wrapped lines has to be said explicitly.
  centered: {
    textAlign: "center",
  },
});
